import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMarkAllRead, useMarkRead, useNotifications, useNotificationStream } from "@/api/notify";
import type { NotificationView } from "@/api/types/NotificationView";
import { relativeTime } from "@/lib/format";
import { usePushNavigation } from "@/lib/push";
import { Icon } from "./Icon";
import { Avatar, cx, Spinner } from "./ui";

/** Bell with unread count and a popover of the latest notifications. Live via the notification stream. */
export function NotificationBell({ tone = "nav", align = "left" }: { tone?: "nav" | "surface"; align?: "left" | "right" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const list = useNotifications(20);
  const unread = list.data?.unread ?? 0;
  const ref = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const pos = usePopoverPosition(ref, open, align);
  useNotificationStream();
  // The bell is on every signed-in screen, so it also routes push clicks.
  usePushNavigation();
  useUnreadTitle(unread);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread ? `${t("notifications.open")} · ${t("notifications.unreadCount", { count: unread })}` : t("notifications.open")}
        className={cx(
          "relative rounded-md p-2",
          tone === "nav" ? "text-nav-muted hover:bg-nav-active hover:text-nav-fg" : "text-muted hover:bg-surface-2 hover:text-text",
        )}
      >
        <Icon name="bell" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] rounded-full bg-[#b42318] px-1 text-center text-[12px] leading-[18px] font-semibold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      <span className="sr-only" aria-live="polite">
        {unread > 0 ? t("notifications.unreadCount", { count: unread }) : ""}
      </span>

      {open &&
        pos &&
        // Portaled: the sidebar is sticky and scrolls, which would clip the panel and leave it under the main area.
        createPortal(
          <div
            ref={panel}
            role="dialog"
            aria-label={t("notifications.title")}
            style={{ top: pos.top, left: pos.left, width: pos.width }}
            className="fixed z-50 flex flex-col overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface text-text shadow-[var(--shadow)]"
          >
            <Header unread={unread} />
            <div className="max-h-[min(480px,calc(100vh-140px))] overflow-y-auto">
              {list.isPending ? (
                <div className="flex justify-center p-6">
                  <Spinner />
                </div>
              ) : list.data?.items.length ? (
                <ul className="m-0 list-none p-0">
                  {list.data.items.map((n) => (
                    <li key={n.id}>
                      <NotificationItem n={n} onOpen={() => setOpen(false)} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="m-0 px-4 py-6 text-center text-sm text-muted">{t("notifications.empty")}</p>
              )}
            </div>
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="border-t border-border px-4 py-2.5 text-center text-sm font-medium no-underline hover:bg-surface-2"
            >
              {t("notifications.seeAll")}
            </Link>
          </div>,
          document.body,
        )}
    </div>
  );
}

function Header({ unread }: { unread: number }) {
  const { t } = useTranslation();
  const mark = useMarkAllRead();
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
      <b className="text-[16px]">{t("notifications.title")}</b>
      {unread > 0 && (
        <button
          type="button"
          disabled={mark.isPending}
          onClick={() => mark.mutate()}
          className="text-sm font-medium text-accent hover:underline disabled:opacity-60"
        >
          {t("notifications.markAllRead")}
        </button>
      )}
    </div>
  );
}

/** One notification row: marks it read and opens its link. */
export function NotificationItem({ n, onOpen }: { n: NotificationView; onOpen?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mark = useMarkRead();
  const open = () => {
    if (!n.read) mark.mutate(n.id);
    onOpen?.();
    void navigate(n.link);
  };
  return (
    <button
      type="button"
      onClick={open}
      className={cx(
        "flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left last:border-b-0 hover:bg-surface-2",
        !n.read && "bg-accent-soft",
      )}
    >
      <Avatar name={n.actor?.display_name ?? "Mixla"} bot={n.actor?.is_bot} size={32} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={cx("text-sm leading-snug", !n.read && "font-semibold")}>{n.title}</span>
        {n.body && <span className="line-clamp-2 text-sm leading-snug text-muted">{n.body}</span>}
        <span className="text-sm text-muted">
          <span className="font-mono">{n.project_key}</span> · {relativeTime(n.created_at)}
        </span>
      </span>
      {!n.read && (
        <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent">
          <span className="sr-only">{t("notifications.unread")}</span>
        </span>
      )}
    </button>
  );
}

/** Viewport position for the portaled panel: under the bell, aligned to its left or right edge, kept on screen. */
function usePopoverPosition(anchor: RefObject<HTMLElement | null>, open: boolean, align: "left" | "right") {
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) return setPos(null);
    const place = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(400, window.innerWidth - 24);
      const left = align === "left" ? r.left : r.right - width;
      setPos({ top: r.bottom + 6, left: Math.max(12, Math.min(left, window.innerWidth - width - 12)), width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchor, open, align]);
  return pos;
}

/** "(3) Mixla" in the browser tab while there are unread notifications. */
function useUnreadTitle(unread: number) {
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, "");
    document.title = unread > 0 ? `(${unread > 99 ? "99+" : unread}) ${base}` : base;
  }, [unread]);
}
